import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form, Input, Radio, Upload, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { DatePickerF, SelectF } from '@/i2antdV5'
import { formatDayString } from '@/utils/renderUtils'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { TUINA_GENDER_OPTIONS, TUINA_THERAPIST_STATUS_OPTIONS } from '@/views/tuina/options'

const FormItem = Form.Item
const IMAGE_MAX_MB = 5
const COVER_MIME = {
  'image/jpeg': true,
  'image/png': true,
  'image/gif': true,
  'image/webp': true
}

function normUploadFileList (e) {
  if (Array.isArray(e)) return e
  return e && e.fileList ? e.fileList : []
}

class TherapistForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    actType: PropTypes.oneOf(['create', 'edit']),
    record: PropTypes.object,
    getJStoreList: PropTypes.object,
    therapistOnDuty: PropTypes.object,
    dispatch: PropTypes.func
  }

  componentDidMount () {
    const { dispatch, getJStoreList } = this.props
    const storeOptions = getJStoreList.data
    if (!Array.isArray(storeOptions) || storeOptions.length === 0) {
      dispatch(fetchJStoreList({ query: {}, dataType: 'optionTuina' }))
    }
    this.syncRecord()
  }

  componentDidUpdate (prevProps) {
    const prevId = prevProps.record && prevProps.record.id
    const nextId = this.props.record && this.props.record.id
    if (prevProps.actType !== this.props.actType || prevId !== nextId) {
      this.syncRecord()
      return
    }
    const prevList = prevProps.getJStoreList.data
    const nextList = this.props.getJStoreList.data
    const prevEmpty = !Array.isArray(prevList) || prevList.length === 0
    const nextHas = Array.isArray(nextList) && nextList.length > 0
    if (prevEmpty && nextHas) this.syncRecord()
  }

  syncRecord = () => {
    const { form, actType, record, therapistOnDuty, getJStoreList } = this.props
    if (!form || !form.current) return
    const storeOptions = getJStoreList.data
    const firstStore = Array.isArray(storeOptions) && storeOptions[0]
    const firstStoreId = firstStore && firstStore.id != null && firstStore.id !== ''
      ? String(firstStore.id)
      : undefined
    const values = {
      gender: 0,
      avatar: [],
      storeId: firstStoreId
    }
    const queryStoreId = therapistOnDuty.query.storeId
    if (queryStoreId != null && queryStoreId !== '') {
      values.storeId = String(queryStoreId)
    }
    if (actType === 'edit' && record) {
      const avatarUrl = record.avatarUrl
      values.storeId = record.storeId == null || record.storeId === ''
        ? undefined
        : String(record.storeId)
      values.name = record.name
      values.phone = record.phone
      values.gender = record.gender ?? 0
      values.status = record.status || 'on_duty'
      values.resignedAt = record.resignedAt ? formatDayString(record.resignedAt) : undefined
      values.intro = record.intro
      values.avatar = avatarUrl
        ? [{ uid: '-1', name: 'avatar', status: 'done', url: avatarUrl }]
        : []
    }
    form.current.setFieldsValue(values)
  }

  beforeUploadImage = (file) => {
    const type = file && file.type ? String(file.type) : ''
    if (!COVER_MIME[type]) {
      message.error('仅支持 jpg / png / gif / webp')
      return Upload.LIST_IGNORE
    }
    const sizeMb = file.size / 1024 / 1024
    if (sizeMb >= IMAGE_MAX_MB) {
      message.error(`图片不能超过 ${IMAGE_MAX_MB}MB`)
      return Upload.LIST_IGNORE
    }
    return false
  }

  render () {
    const { form, actType, getJStoreList } = this.props
    const storeOptions = getJStoreList.data
    return (
      <Form ref={form} layout="vertical">
        <FormItem
          label="门店"
          name="storeId"
          rules={[{ required: true, message: '请选择门店' }]}
        >
          <SelectF
            options={storeOptions}
            modalProps={{
              size: 'large',
              placeholder: '请选择门店',
              disabled: actType === 'edit'
            }}
          />
        </FormItem>
        <FormItem noStyle shouldUpdate={(prev, cur) => prev.avatar !== cur.avatar}>
          {() => {
            const fileList = (form && form.current && form.current.getFieldValue('avatar')) || []
            return (
              <FormItem
                label="头像"
                name="avatar"
                valuePropName="fileList"
                getValueFromEvent={normUploadFileList}
                extra="支持 jpg / png / gif / webp，最多 1 张，不超过 5MB"
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  beforeUpload={this.beforeUploadImage}
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  )}
                </Upload>
              </FormItem>
            )
          }}
        </FormItem>
        <FormItem
          label="姓名"
          name="name"
          rules={[{ required: true, message: '请填写姓名' }]}
        >
          <Input size="large" placeholder="如 张三" />
        </FormItem>
        <FormItem
          label="手机号"
          name="phone"
          rules={[{ required: true, message: '请填写手机号' }]}
        >
          <Input size="large" placeholder="师傅手机号" />
        </FormItem>
        <FormItem
          label="性别"
          name="gender"
          rules={[{ required: true, message: '请选择性别' }]}
        >
          <Radio.Group size="large" optionType="button" buttonStyle="solid">
            {TUINA_GENDER_OPTIONS.map((item) => (
              <Radio.Button key={item.id} value={item.id}>{item.value}</Radio.Button>
            ))}
          </Radio.Group>
        </FormItem>
        {actType === 'edit' ? (
          <>
            <FormItem
              label="状态"
              name="status"
              rules={[{ required: true, message: '请选择状态' }]}
            >
              <Radio.Group size="large" optionType="button" buttonStyle="solid">
                {TUINA_THERAPIST_STATUS_OPTIONS.map((item) => (
                  <Radio.Button key={item.id} value={item.id}>{item.value}</Radio.Button>
                ))}
              </Radio.Group>
            </FormItem>
            <FormItem
              label="离岗日期"
              name="resignedAt"
              dependencies={['status']}
              rules={[
                ({ getFieldValue }) => ({
                  required: getFieldValue('status') === 'off_duty',
                  message: '请选择离岗日期'
                })
              ]}
            >
              <DatePickerF
                dateStrValue
                modalProps={{
                  size: 'large',
                  allowClear: true,
                  format: 'YYYY-MM-DD',
                  placeholder: '请选择离岗日期',
                  style: { width: '100%' },
                  getPopupContainer: () => document.body,
                  popupStyle: { zIndex: 1200 }
                }}
              />
            </FormItem>
          </>
        ) : null}
        <FormItem
          label="简介"
          name="intro"
        >
          <Input.TextArea
            rows={3}
            size="large"
            placeholder="选填"
            allowClear
            maxLength={512}
            showCount
          />
        </FormItem>
      </Form>
    )
  }
}

const mapStateToProps = (state) => ({
  getJStoreList: state.components.getJStoreList,
  therapistOnDuty: state.tuina.therapistOnDuty
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(TherapistForm)
